import { type Dispatch, type RefObject, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import type { CommandItem } from "./commandItem";
import { filterCommands } from "./filterCommands";

export interface PaletteResults {
  query: string;
  setQuery: (query: string) => void;
  results: CommandItem[];
  /** Index of the highlighted result. */
  active: number;
  setActive: Dispatch<SetStateAction<number>>;
  listRef: RefObject<HTMLDivElement | null>;
}

/**
 * The search state: each opening starts from `initialQuery` with the first result highlighted; a
 * new query highlights the first result again; the highlighted result is kept scrolled into view.
 */
export function usePaletteResults(items: CommandItem[], open: boolean, initialQuery: string): PaletteResults {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => filterCommands(items, query), [items, query]);

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    setActive(0);
    // Seed only when opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  return { query, setQuery, results, active, setActive, listRef };
}
